import { useState, useEffect } from 'react'
import {
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Lock
} from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import { Button, Card, Modal, ConfirmModal } from '../common'
import CategoryForm from './CategoryForm'
import PromptEditor from './PromptEditor'
import useCategoryStore from '../../stores/categoryStore'

// Map icon names to Lucide components
const getIcon = (iconName) => {
  const iconMap = {
    'home': LucideIcons.Home,
    'trees': LucideIcons.Trees,
    'warehouse': LucideIcons.Warehouse,
    'utensils': LucideIcons.Utensils,
    'sofa': LucideIcons.Sofa,
    'bed-double': LucideIcons.BedDouble,
    'bed': LucideIcons.Bed,
    'bath': LucideIcons.Bath,
    'shower-head': LucideIcons.ShowerHead,
    'door-open': LucideIcons.DoorOpen,
    'flower-2': LucideIcons.Flower2,
    'car': LucideIcons.Car,
    'gauge': LucideIcons.Gauge,
    'folder': LucideIcons.Folder,
    'camera': LucideIcons.Camera,
    'image': LucideIcons.Image,
    'building': LucideIcons.Building,
    'hammer': LucideIcons.Hammer,
    'thermometer': LucideIcons.Thermometer,
    'droplets': LucideIcons.Droplets
  }
  return iconMap[iconName] || LucideIcons.Folder
}

// Section labels
const SECTION_LABELS = {
  D: 'D - Outside condition',
  E: 'E - Inside condition',
  F: 'F - Services',
  G: 'G - Grounds'
}

export default function CategoryManager() {
  const [editingCategory, setEditingCategory] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [showPromptEditor, setShowPromptEditor] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [expandedCategory, setExpandedCategory] = useState(null)

  const categories = useCategoryStore(state => state.categories)
  const customCategories = useCategoryStore(state => state.customCategories)
  const loading = useCategoryStore(state => state.loading)
  const deleteCategory = useCategoryStore(state => state.deleteCategory)
  const loadCategories = useCategoryStore(state => state.loadCategories)

  // Load custom categories on mount
  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  const handleAddNew = () => {
    setEditingCategory(null)
    setShowForm(true)
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setShowForm(true)
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await deleteCategory(deleteConfirm.id)
      setDeleteConfirm(null)
    } catch (error) {
      console.error('Failed to delete category:', error)
    }
  }

  // Render a category card
  const renderCategoryCard = (category, isCustom = false) => {
    const Icon = getIcon(category.icon)
    const isExpanded = expandedCategory === (category.id || category.code)
    const categoryKey = category.id || category.code

    return (
      <Card
        key={categoryKey}
        padding="none"
        className="transition-all"
      >
        {/* Main row */}
        <div className="flex items-center gap-3 p-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            isCustom ? 'bg-amber-100 text-amber-700' : 'bg-primary-100 text-primary-700'
          }`}>
            <Icon className="w-6 h-6" />
          </div>

          {/* Name and info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900 truncate">
                {category.code} - {category.name}
              </h3>
              {!isCustom && (
                <Lock className="w-4 h-4 text-slate-400" title="Built-in category" />
              )}
              {isCustom && category.section && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                  Section {category.section}
                </span>
              )}
              {isCustom && !category.section && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                  Standalone
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 truncate">
              {category.aiPrompt ? 'AI prompt configured' : 'No AI prompt set'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setExpandedCategory(isExpanded ? null : categoryKey)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              title="View prompt"
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setShowPromptEditor({ ...category, isCustom })}
              className="p-2 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              title="Edit AI prompt"
            >
              <LucideIcons.Sparkles className="w-5 h-5" />
            </button>
            {isCustom && (
              <>
                <button
                  onClick={() => handleEdit(category)}
                  className="p-2 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Edit category"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(category)}
                  className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete category"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Expanded prompt view */}
        {isExpanded && category.aiPrompt && (
          <div className="px-4 pb-4 pt-0">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2">AI Prompt:</p>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">
                {category.aiPrompt}
              </p>
            </div>
          </div>
        )}
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Survey Categories</h2>
          <p className="text-slate-500 mt-1">
            Manage categories and AI prompts for property surveys
          </p>
        </div>
        <Button variant="primary" icon="Plus" onClick={handleAddNew}>
          Add Custom Category
        </Button>
      </div>

      {/* Custom Categories Section */}
      {customCategories.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
            <LucideIcons.Star className="w-5 h-5 text-amber-500" />
            Custom Categories
          </h3>
          {customCategories.map((category) => renderCategoryCard(category, true))}
        </div>
      )}

      {/* Built-in Categories Section */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
          <Lock className="w-5 h-5 text-slate-400" />
          Built-in Categories
          <span className="text-sm font-normal text-slate-500">(prompt editing only)</span>
        </h3>
        {categories.map((category) => renderCategoryCard({ ...category, isStatic: true }, false))}

        {categories.length === 0 && customCategories.length === 0 && (
          <Card className="text-center py-12">
            <LucideIcons.FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No categories yet</h3>
            <p className="text-slate-500 mb-4">Add your first custom category to get started</p>
            <Button variant="primary" icon="Plus" onClick={handleAddNew}>
              Add Custom Category
            </Button>
          </Card>
        )}
      </div>

      {/* Add/Edit Category Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingCategory ? 'Edit Custom Category' : 'Add Custom Category'}
        size="md"
      >
        <CategoryForm
          category={editingCategory}
          onClose={() => setShowForm(false)}
        />
      </Modal>

      {/* Prompt Editor Modal */}
      <Modal
        isOpen={!!showPromptEditor}
        onClose={() => setShowPromptEditor(null)}
        title={`AI Prompt - ${showPromptEditor?.name}`}
        size="lg"
      >
        {showPromptEditor && (
          <PromptEditor
            category={showPromptEditor}
            onClose={() => setShowPromptEditor(null)}
          />
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Custom Category"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        loading={loading}
      />
    </div>
  )
}
